import React, { useEffect, useMemo, useState } from "react";
import { message, Spin } from "antd";
import "./index.css";
import * as com from "../../Common";
import { track } from "statsig";
import { STATSIG, PAGE } from "statsig/events";

type BaseStyle = "loan_officer" | "underwriter";

const OPTIONS: Array<{
  value: BaseStyle;
  title: string;
  description: string;
}> = [
  {
    value: "loan_officer",
    title: "Concise answers, prioritizing efficiency and immediate clarity.",
    description: "Ideal for loan officer",
  },
  {
    value: "underwriter",
    title: "Detailed answers, prioritizing depth and exhaustivity.",
    description: "Ideal for underwriter",
  },
];

const isBaseStyle = (value: string | null): value is BaseStyle =>
  value === "loan_officer" || value === "underwriter";

type GetProfileResponse = {
  Status?: string;
  Error?: string;
  LoanOfficers?: Array<{ ID?: string }>;
};

type UserPreferenceResponse = {
  data: {
    answer_audience?: BaseStyle;
  };
};

const getAuthHeaders = (extraHeaders: Record<string, string> = {}) => {
  const token = sessionStorage.getItem("ZeitroA");
  return {
    Authorization: `Bearer ${token}`,
    Cache: "no-cache",
    Customer: com.getCustomerId() || "",
    ...extraHeaders,
  };
};

const AIBaseStyle = () => {
  const [value, setValue] = useState<BaseStyle>("loan_officer");
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState("");

  const selectedItem = useMemo(
    () => OPTIONS.find((item) => item.value === value),
    [value],
  );

  useEffect(() => {
    track(STATSIG.COMMON.VIEW, PAGE.SETTINGS_STRATA_AI, { page: PAGE.SETTINGS_STRATA_AI })
    let mounted = true;

    const init = async () => {
      setLoading(true);
      try {
        const profileResponse = await fetch("/los/getprofile", {
          method: "GET",
          headers: getAuthHeaders(),
        });
        if (profileResponse.status !== 200) {
          throw new Error(`getprofile failed: ${profileResponse.status}`);
        }
        const profile = (await profileResponse.json()) as GetProfileResponse;
        if (profile?.Status && profile.Status !== "OK") {
          throw new Error(profile?.Error || "getprofile failed");
        }

        const nextUserId = profile?.LoanOfficers?.[0]?.ID || "";
        if (!nextUserId) return;
        if (mounted) setUserId(nextUserId);

        const preferenceResponse = await fetch(
          `${com.getStrataApiBaseUrl()}/mai/api/user/preferences`,
          {
            method: "GET",
            headers: getAuthHeaders({ User: nextUserId }),
          },
        );
        const preference = preferenceResponse.ok
          ? ((await preferenceResponse.json()) as UserPreferenceResponse)
          : undefined;

        if (!mounted) return;
        if (isBaseStyle(preference?.data?.answer_audience ?? null)) {
          setValue(preference?.data?.answer_audience);
        }
      } catch (error) {
        console.error(error);
        message.error("Failed to load preference");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    init();
    return () => {
      mounted = false;
    };
  }, []);

  const onChange = async (next: BaseStyle) => {
    if (loading || next === value || !userId) return;
    const previous = value;
    setValue(next);
    setLoading(true);
    try {
      const response = await fetch(
        `${com.getStrataApiBaseUrl()}/mai/api/user/preferences`,
        {
          method: "PUT",
          headers: getAuthHeaders({
            "Content-Type": "application/json",
            User: userId,
          }),
          body: JSON.stringify({ answer_audience: next }),
        },
      );
      if (!response.ok) {
        throw new Error(`update preference failed: ${response.status}`);
      }
      const updated = (await response.json()) as UserPreferenceResponse;
      setValue(updated?.data?.answer_audience ?? next);
      track(STATSIG.STRATA_AI.ANSWER_STYLE, next, { audience: next });
      message.success("Changes saved");
    } catch (error) {
      setValue(previous);
      console.error(error);
      message.error("Update failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="ai-base-style">
      <div className="ai-base-style__content">
        <div className="ai-base-style__body">
          <div className="User_title mt-4 mb-3 mr-5 Support">
            <div>Strata AI</div>
          </div>
          <div className="Customize ai-base-style__description">
            Set the style and tone of how Zeitro Strata responds to you. This
            doesn&apos;t impact Zeitro Strata&apos;s capabilities.
          </div>

          <Spin spinning={loading}>
            <div
              className="ai-base-style__options"
              role="radiogroup"
              aria-label="Strata AI"
            >
              {OPTIONS.map((item) => {
                const selected = item.value === value;

                return (
                  <button
                    key={item.value}
                    type="button"
                    className={`ai-base-style__option ${selected ? "is-selected" : ""}`}
                    aria-pressed={selected}
                    onClick={() => onChange(item.value)}
                    disabled={loading}
                  >
                    <span className="ai-base-style__option-content">
                      <strong>{item.title}</strong>
                      <span>{item.description}</span>
                    </span>
                    <span className="ai-base-style__check" aria-hidden="true">
                      {selected ? "✓" : null}
                    </span>
                  </button>
                );
              })}
            </div>
          </Spin>

          {selectedItem ? (
            <div className="ai-base-style__summary">
              Current choice: <strong>{selectedItem.description}</strong>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default AIBaseStyle;
